<template>
  <div class="dispute-activities">
    <div v-if="dataLength">
      <div
        v-for="(item, index) in activities"
        :key="index"
        class="dispute-activities__container">
        <accordian
          :expanded="item.expanded"
          :item="item"
          :index="index"
          @onCardToggle="onAccordianToggle">
          <div
            slot="accordian-header-content"
            class="dispute-activities__container__accordian-header">
            <div
              class="dispute-activities__container__accordian-header__content">
              <avatar
                class="dispute-activities__container__accordian-header__content__avatar"
                :user-name="getUserForActivity(item)"
                :radius="15" />
              <div
                class="dispute-activities__container__accordian-header__content__info">
                <span
                  class="dispute-activities__container__accordian-header__content__info__name--bold">
                  {{ getUserForActivity(item) }}
                </span>
                <span v-if="item.commitMetadata.commitDateInstant">{{
                  formatDate(item.commitMetadata.commitDateInstant)
                }}</span>
              </div>
            </div>
            <div
              class="dispute-activities__container__accordian-header__items-count">
              <span>
                Changed {{ item.changedProperties.length }} properties
              </span>
            </div>
          </div>
          <div
            v-if="item.expanded"
            slot="accordian-body"
            class="dispute-activities__container__accordian-body">
            <div
              v-for="property in item.changedProperties"
              :key="property"
              class="dispute-activities__container__accordian-body__item">
              <span
                class="dispute-activities__container__accordian-body__item--title">
                {{ getPropertyDisplayName(property) }}
              </span>
              <span
                v-if="property === 'voiceSummary'"
                class="dispute-activities__container__accordian-body__item--value"
                v-html="
                  getPropertyDisplayValue(item.state[property], property)
                "></span>
              <span
                v-else-if="property === 'disputedChargeDetails'"
                class="dispute-activities__container__accordian-body__item--value">
                <span
                  v-for="property in caseDetails.disputedChargeDetails"
                  :key="property">
                  <span class="chargeDetailsValue">
                    Charge name: {{ property.chargeName }}
                  </span>
                  <span class="chargeDetailsValue">
                    Dispute category:
                    {{ property.disputeCategory.label }}
                  </span>
                  <span class="chargeDetailsValue">
                    Current amount: {{ property.currency }}
                    {{ property.currentAmount }}
                  </span>
                  <span class="chargeDetailsValue">
                    Expected amount: {{ property.currency }}
                    {{ property.expectedAmount }}
                  </span>
                  <span class="chargeDetailsValue">
                    Disputed amount: {{ property.currency }}
                    {{ property.disputedAmount }}
                  </span>
                </span>
              </span>
              <span
                v-else
                class="dispute-activities__container__accordian-body__item--value">
                {{ getPropertyDisplayValue(item.state[property], property) }}
              </span>
            </div>
          </div>
        </accordian>
      </div>
    </div>
    <h4 v-else class="dispute-activities__not-found">No Audit History Found</h4>
  </div>
</template>

<script>
// Utils
import { format } from "date-fns";

import { stateToDisplayNameMapping } from "@/config/constant.js";

import { getZonedTime } from "@/utils/helpers";

export default {
  name: "DisputeActivities",
  props: {
    activities: {
      type: Array,
      required: true,
    },
    user: {
      type: Object,
      required: true,
    },
    caseDetails: {
      type: Object,
      default: () => {},
    },
  },
  computed: {
    dataLength() {
      return this.activities?.length > 0;
    },
  },
  methods: {
    formatDate(date, element) {
      try {
        date =
          date[date.length - 1] === "Z"
            ? date
            : date.indexOf("T") > -1
            ? `${date}Z`
            : getZonedTime(date);
        return date
          ? element?.format
            ? format(new Date(`${date}`), "dd-MMM-yyyy")
            : format(new Date(`${date}`), "dd-MMM-yyyy hh:mm a")
          : "N/A";
      } catch (err) {
        return date;
      }
    },
    onAccordianToggle(data, index) {
      event.stopPropagation();
      let newArray = [...this.activities];
      newArray[index] = {
        ...data,
        expanded: !data.expanded,
      };
      this.activities = newArray;
    },
    getPropertyDisplayName(property) {
      const element = stateToDisplayNameMapping[property];
      return element ? element.displayName : property;
    },
    getPropertyDisplayValue(value, property) {
      if (!value || value === "null") {
        return typeof value === "boolean" ? value : "-";
      }
      const element = stateToDisplayNameMapping[property];
      if (typeof element === "undefined") {
        return value;
      }
      return element.type === "date" ? this.formatDate(value, element) : value;
    },
    getUserForActivity(item) {
      if (item) {
        if (
          "state" in item &&
          item.state &&
          "changedBy" in item.state &&
          item.state.changedBy
        ) {
          return item.state.changedBy;
        }
        return item.commitMetadata.author;
      }
    },
  },
};
</script>

<style lang="scss">
@import "@/styles/imports";

.dispute-activities {
  &__not-found {
    color: var(--Gray-900);
    margin-bottom: 1rem;
  }

  &__container {
    margin: 1rem 0;
    cursor: pointer;
    position: relative;

    &::before {
      content: "";
      position: absolute;
      width: 20px;
      height: 20px;
      background-color: var(--Blue-800);
      border-radius: 24px;
      margin-top: 32px;
    }

    &::after {
      content: "";
      height: calc(100% + 8px);
      position: absolute;
      top: 42px;
      left: 9px;
      width: 2px;
      background-color: var(--Blue-800);
    }

    &:last-child {
      margin-bottom: 0;

      &::after {
        content: none;
      }
    }

    &__accordian-header {
      display: flex;
      align-items: center;

      &__content {
        display: flex;
        align-items: center;

        @include media("<tablet") {
          flex-direction: column;
        }

        &__avatar {
          margin-right: 16px;
        }

        &__info {
          display: flex;
          flex-direction: column;
          margin-right: 2rem;
          font-family: "Maersk Text";
          font-size: 14px;
          color: var(--Blue-900-Darkest);
          text-transform: capitalize;

          &__name {
            font-size: 16px;
            margin-bottom: 4px;
            &--bold {
              font-weight: bold;
            }
          }
        }
      }
      &__items-count {
        font-size: 18px;
        margin-left: 16px;
      }
    }

    &__accordian-body {
      padding: 1rem;
      border: 1px solid var(--Gray-200);
      border-bottom-left-radius: 4px;
      border-bottom-right-radius: 4px;
      display: flex;
      flex-direction: column;
      margin-left: 60px;
      cursor: default;

      @include media("<tablet") {
        margin-left: 40px;
      }

      &__item {
        display: flex;
        margin-bottom: 8px;
        &--title {
          color: var(--Gray-600);
          display: flex;
          flex-direction: column;
          flex-basis: 100%;
          flex: 1;
        }
        &--value {
          display: flex;
          flex-direction: column;
          flex-basis: 100%;
          flex: 1;
          flex: 2;
        }
      }
    }
  }
  .chargeDetailsValue {
    display: block;
  }
}
</style>
